import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import AdminQuoteTemplateTable from "../../quoteTemplate/components/AdminQuoteTemplateTable";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Button, Form, FormGroup, Label, Input, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
import { fetchSingleAccount, fetchSingleOpportunity } from "../../opportunities/data/opportunitiesService";
import { useLockState } from "../../../components/userLocks/LockCheck";
import debounce from "lodash/debounce";

const PINNED_SECTION = "Section 1";

const QuoteContent = ({
    quoteRevision,
    templatesData,
    selectedTemplate,
    setSelectedTemplate,
    onUpdateQuoteTemplateId,
    onUpdateQuoteAdditionalFields,
    customContent
}) => {
    const queryClient = useQueryClient();
    const { id } = useParams();
    const lockState = useLockState();

    const { data: opportunityFull } = useQuery({
        queryKey: ["opportunitySingle", id],
        queryFn: () => fetchSingleOpportunity(id),
        staleTime: 60000,
    });

    const { data: accountFull } = useQuery({
        queryKey: ["accountSingle", id],
        queryFn: () => fetchSingleAccount(id),
        staleTime: 60000,
    });

    const [sectionValues, setSectionValues] = useState({});
    const [saveStatus, setSaveStatus] = useState(null); // "saving" | "saved" | "error" | null

    const suppressHydrateRef = useRef(false);
    const hasLocalEditsRef = useRef(false);

    // ---- Race/loop guards ----
    const lastSentRef = useRef(""); // dedupe: skip resending identical values
    const pendingSaveRef = useRef(null); // queue latest while saving

    // ---- Hydration guard (prevents saving blanks on refresh) ----
    const hydratedTemplateIdRef = useRef(null);

    // ------------------- Helpers -------------------
    const coerceToString = (v) => (v === null || v === undefined ? "" : String(v));
    const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";
    const normalizeSectionName = (s) => (s || "").trim() || PINNED_SECTION;

    const parseSections = (tpl) => {
        if (!tpl?.customSections) return [];
        try {
            const raw = tpl.customSections;
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            console.error("Failed to parse customSections");
            return [];
        }
    };

    const parseSectionsConfig = (tpl) => {
        if (!tpl?.customSectionsConfig) return [];
        try {
            const raw = tpl.customSectionsConfig;
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            console.error("Failed to parse customSectionsConfig");
            return [];
        }
    };

    const customSections = useMemo(() => parseSections(selectedTemplate), [selectedTemplate]);
    const customSectionsConfig = useMemo(() => parseSectionsConfig(selectedTemplate), [selectedTemplate]);

    // Used to determine whether sectionValues is "complete enough" to autosave
    const expectedFieldKeys = useMemo(() => customSections.map((s) => s.field), [customSections]);

    const orderedTabsFromConfig = useMemo(() => {
        return [...customSectionsConfig]
            .map((c) => ({
                name: normalizeSectionName(c?.name),
                sortOrder: typeof c?.sortOrder === "number" ? c.sortOrder : 0,
            }))
            .filter((c) => !!c.name)
            .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
            .map((c) => c.name);
    }, [customSectionsConfig]);

    const sectionTabs = useMemo(() => {
        const fromFields = [...new Set(customSections.map((s) => normalizeSectionName(s.section)))];
        const tabs = [...orderedTabsFromConfig];
        for (const t of fromFields) {
            if (!tabs.includes(t)) tabs.push(t);
        }
        if (tabs.includes(PINNED_SECTION)) {
            return [PINNED_SECTION, ...tabs.filter((t) => t !== PINNED_SECTION)];
        }
        return tabs;
    }, [customSections, orderedTabsFromConfig]);

    const sectionsByTab = useMemo(() => {
        const map = new Map();
        for (const s of customSections) {
            const key = normalizeSectionName(s.section);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        for (const [, arr] of map.entries()) {
            arr.sort(
                (a, b) =>
                    (typeof a?.sortOrder === "number" ? a.sortOrder : 0) -
                    (typeof b?.sortOrder === "number" ? b.sortOrder : 0) ||
                    (a.label || "").localeCompare(b.label || "")
            );
        }
        return map;
    }, [customSections]);

    const hydrateSectionValuesFrom = (tpl, revision) => {
        const sections = parseSections(tpl);
        const initial = {};

        sections.forEach((s) => {
            let existing = Array.isArray(revision?.quoteAdditionalFields)
                ? revision.quoteAdditionalFields.find((x) => x.field === s.field)?.value
                : undefined;

            if (existing === "" || existing === undefined)
                existing = null;

            if (s.region) {
                initial[s.field] = existing == null ? true : toBool(existing);
                return;
            }

            let defaultValue = null;
            if (s.salesforceDefaultValue) {
                defaultValue =
                    opportunityFull?.[s.salesforceDefaultValue.replace("opportunity_", "")] ??
                    accountFull?.[s.salesforceDefaultValue.replace("account_", "")] ??
                    "";
                if (s.type === "TextArea") defaultValue = "<p>" + defaultValue + "</p>";
            } else {
                defaultValue = s.defaultValue;
            }

            initial[s.field] = coerceToString(existing ?? defaultValue ?? "");
        });

        return initial;
    };

    const markHydrated = useCallback((tplId, values) => {
        hydratedTemplateIdRef.current = tplId ?? null;
        hasLocalEditsRef.current = false;

        // IMPORTANT: prevent autosave from firing for this freshly-hydrated state
        lastSentRef.current = JSON.stringify(values ?? {});
        pendingSaveRef.current = null;
    }, []);

    const isHydratedForCurrentTemplate = () => {
        const tplId = selectedTemplate?.id ?? null;
        return tplId && hydratedTemplateIdRef.current === tplId;
    };

    const isSectionValuesCompleteEnough = () => {
        if (!expectedFieldKeys.length) return false;
        for (const k of expectedFieldKeys) {
            if (!(k in (sectionValues ?? {}))) return false;
        }
        return true;
    };

    // ------------------- Initial selection from server -------------------
    useEffect(() => {
        if (!opportunityFull || !accountFull) return;
        if (suppressHydrateRef.current) return;
        if (!templatesData?.length || !quoteRevision) return;

        const templateId = quoteRevision?.quoteTemplateId ?? quoteRevision?.selectedTemplateId ?? null;
        if (!templateId) return;
        if (selectedTemplate?.id === templateId) return;

        const match = templatesData.find((t) => t.id === templateId);
        if (match) {
            setSelectedTemplate(match);

            // hydrate immediately and mark hydrated so autosave won't run with blanks
            const initial = hydrateSectionValuesFrom(match, quoteRevision);
            setSectionValues((prev) => (Object.keys(prev).length === 0 ? initial : prev));
            markHydrated(match.id, initial);
        }
    }, [
        templatesData,
        quoteRevision,
        selectedTemplate,
        opportunityFull,
        accountFull,
        setSelectedTemplate,
        markHydrated,
        customContent
    ]);

    useEffect(() => {
        const quoteAdditionalFieldsChanged = quoteRevision?.quoteAdditionalFields
            ? quoteRevision.quoteAdditionalFields.some((f) => f.value !== customContent.find(c => c.id === f.id)?.value)
            : true;


        if (quoteAdditionalFieldsChanged) {
            const templateCustomSections = selectedTemplate?.customSections ? JSON.parse(selectedTemplate.customSections) : [];

            const newValues = Object.fromEntries(
                    customContent
                        .filter(x => templateCustomSections.map(s => s.field).includes(x.field))
                        .map(x => [x.field, x.value])
                )

            setSectionValues(newValues)            
        }
    }, [customContent])


    // ------------------- Mutations -------------------
    const saveTemplateMutation = useMutation({
        mutationFn: async (tpl) => {
            return onUpdateQuoteTemplateId(tpl?.id ?? null);
        },
        onSuccess: (payload) => {
           
        },
    });

    const saveSectionsMutation = useMutation({
        mutationFn: async ({ tpl, values }) => {
            const payload = structuredClone(quoteRevision ?? {});
            payload.quoteTemplateId = tpl?.id ?? payload.quoteTemplateId ?? null;

            payload.quoteAdditionalFields = customSections.map((s) => ({
                id: s.id,
                field: s.field,
                value: s.region ? String(!!values?.[s.field]) : coerceToString(values?.[s.field] ?? ""),
            }));

            return payload;
        },
        onMutate: () => setSaveStatus("saving"),
        onSuccess: (payload, variables) => {
            onUpdateQuoteTemplateId(variables?.tpl?.id ?? null);
            onUpdateQuoteAdditionalFields(payload.quoteAdditionalFields);

            if (pendingSaveRef.current) {
                setSaveStatus("saving");
            } else {
                setTimeout(() => setSaveStatus("saved"), 1000);
                setTimeout(() => setSaveStatus(null), 1200);
            }


        },
        onError: () => setSaveStatus("error"),
    });

    // ------------------- Handlers -------------------
    const handleSelectTemplate = (tpl) => {
        // suppressHydrateRef guards the "initial selection" useEffect, which normally runs on page load
        // to auto-select the template stored in quoteRevision.quoteTemplateId and hydrate the fields from
        // the server data. We need it suppressed here because:
        //
        // When the user picks a NEW template, saveTemplateMutation.mutate() is called below, but
        // quoteRevision.quoteTemplateId still reflects the OLD template ID until the parent state
        // updates after the async save completes. If suppressHydrateRef were false, the initial-selection
        // useEffect would immediately re-run, see a mismatch between selectedTemplate.id (new) and
        // quoteRevision.quoteTemplateId (old), re-select the old template, and call markHydrated() with
        // the old template's ID — clobbering hydratedTemplateIdRef so it no longer matches the new
        // template and causing the autosave guard to permanently skip saves.
        //
        // By setting it to true here, we prevent the initial-selection useEffect from interfering until
        // the user explicitly resets (via resetSelectedTemplate), at which point suppressHydrateRef is
        // set back to false. At that point quoteRevision.quoteTemplateId will be null (the reset clears
        // it), so the initial-selection effect will exit early via `if (!templateId) return` rather than
        // re-selecting anything.
        suppressHydrateRef.current = true;

        setSelectedTemplate(tpl);

        const initial = hydrateSectionValuesFrom(tpl, quoteRevision);
        setSectionValues(initial);

        // mark hydrated + prevent autosave of initial state
        markHydrated(tpl?.id ?? null, initial);

        saveTemplateMutation.mutate(tpl);
    };

    const setFieldValue = (field, value) => {
        hasLocalEditsRef.current = true;
        setSectionValues((prev) => ({ ...prev, [field]: value }));
    };

    // ---- Save orchestration: dedupe + no overlap + queue latest ----
    const requestSave = useCallback(
        (tpl, values) => {
            if (!tpl || !quoteRevision?.id) return;

            if (saveSectionsMutation.isPending) {
                pendingSaveRef.current = { tpl, values };
                return;
            }

            saveSectionsMutation.mutate({ tpl, values });
        },
        [quoteRevision?.id, saveSectionsMutation]
    );

    useEffect(() => {
        if (saveSectionsMutation.isPending) return;

        if (pendingSaveRef.current) {
            const next = pendingSaveRef.current;
            pendingSaveRef.current = null;

            lastSentRef.current = JSON.stringify(next.values ?? {});
            requestSave(next.tpl, next.values);
        }
    }, [saveSectionsMutation.isPending, requestSave]);

    const maybeSave = useCallback(
        (tpl, values) => {
            if (!tpl) return;

            const snapshot = JSON.stringify(values ?? {});
            if (snapshot === lastSentRef.current) return;

            lastSentRef.current = snapshot;
            requestSave(tpl, values);
        },
        [requestSave]
    );

    // Stable debounce created once, calls latest maybeSave
    const maybeSaveRef = useRef(maybeSave);
    useEffect(() => {
        maybeSaveRef.current = maybeSave;
    }, [maybeSave]);

    const debouncedSave = useMemo(
        () =>
            debounce((tpl, values) => {
                maybeSaveRef.current(tpl, values);
            }, 500),
        []
    );

    useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

    // ✅ Autosave effect with hydration + completeness guards
    useEffect(() => {
        if (!selectedTemplate) return;

        // Do not autosave until we have hydrated for this template (prevents refresh-blank overwrite)
        if (!isHydratedForCurrentTemplate()) return;

        // Do not autosave until values are populated for all expected fields
        if (!isSectionValuesCompleteEnough()) return;

        console.log("DEBOUNCE CALLED");
        debouncedSave(selectedTemplate, sectionValues);
    }, [sectionValues, selectedTemplate, debouncedSave, expectedFieldKeys]); // expectedFieldKeys affects completeness

    const resetSelectedTemplate = () => {
        // Set suppressHydrateRef = false to allow the initial-selection effect to re-run if needed.
        // onUpdateQuoteTemplateId(null) clears quoteRevision.quoteTemplateId, so the initial-selection
        // effect will see templateId = null and exit early via `if (!templateId) return` — 
        // it will NOT re-select the old template.
        suppressHydrateRef.current = false;

        hydratedTemplateIdRef.current = null;
        hasLocalEditsRef.current = false;
        lastSentRef.current = "";
        pendingSaveRef.current = null;

        setSelectedTemplate(null);
        setSectionValues({});
        onUpdateQuoteTemplateId(null)
    };

    // Hydrate only when values are empty AND we haven't started editing locally
    useEffect(() => {
        if (!selectedTemplate || !quoteRevision || !opportunityFull || !accountFull) return;
        if (hasLocalEditsRef.current) return;

        setSectionValues((prev) => {
            if (prev && Object.keys(prev).length) return prev;

            const initial = hydrateSectionValuesFrom(selectedTemplate, quoteRevision);

            // Mark hydrated + prevent autosave on this initial set
            markHydrated(selectedTemplate?.id ?? null, initial);

            return initial;
        });
    }, [selectedTemplate, quoteRevision, opportunityFull, accountFull, markHydrated]);

    const [activeSection, setActiveSection] = useState("");

    useEffect(() => {
        if (!sectionTabs.length) {
            setActiveSection("");
            return;
        }
        setActiveSection((prev) => (prev && sectionTabs.includes(prev) ? prev : sectionTabs[0]));
    }, [sectionTabs]);

    const hasAnyTextContent = v => {
        if (v === null || v === undefined || v === "") return false;
        const d = document.createElement("div");
        d.innerHTML = String(v);
        d.querySelectorAll("br").forEach(b => b.remove());
        return (d.textContent || "").length > 0;
    };

    const generateFieldKey = (field) => {
        const fieldDef = customSections.find(s => s.field === field)

        if (!fieldDef) {
            return field; // fallback to field name if we can't find the definition (shouldn't happen unless template isn't loaded yet)
        }

        if (hasAnyTextContent(fieldDef.defaultValue) || fieldDef.salesforceDefaultValue) {
            // There is a default value so we want to reset to that when content is cleared

            const v = (sectionValues?.[field] ?? "").toString();

            if (!hasAnyTextContent(v)) {
                return field + "_empty";
            }

            return field
        } else {
            // if there's no meaningful default value, use the field name as key so that placeholder doesn't reset when the content becomes empty
            return field;
        }
    }

    // ------------------- Render -------------------
    return (
        <div className={lockState?.isLocked ? "locked" : ""}>
            {!selectedTemplate && (
                <AdminQuoteTemplateTable mode="user" selectTemplate={handleSelectTemplate} key="template-picker" />
            )}

            {selectedTemplate && (
                <>
                    <div className="d-flex align-items-center gap-2 mt-3">
                        <span>
                            <strong>Selected Template:</strong> {selectedTemplate.name}
                        </span>
                        <Button onClick={resetSelectedTemplate} aria-label="Change template">
                            Change
                        </Button>
                    </div>

                    {customSections.length > 0 ? (
                        <>
                            <Form className="mt-3">
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <div style={{ display: "inline-flex" }}>
                                        {sectionTabs.map((tab) => (
                                            <h6
                                                key={tab}
                                                className={activeSection === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => setActiveSection(tab)}
                                            >
                                                {tab}
                                            </h6>
                                        ))}
                                    </div>

                                    <div className="d-flex align-items-center gap-2 mt-2">
                                        {saveStatus === null && (
                                            <span className="text-success small">
                                                Up to date <i className="bi bi-check-circle"></i>
                                            </span>
                                        )}
                                        {saveStatus === "saving" && (
                                            <span className="small">
                                                Currently Saving <Spinner size="sm" />
                                            </span>
                                        )}
                                        {saveStatus === "saved" && (
                                            <span className="text-success small">
                                                Saved <i className="bi bi-check-circle"></i>
                                            </span>
                                        )}
                                        {saveStatus === "error" && (
                                            <span className="text-danger small">
                                                Save failed <i className="bi bi-x-circle"></i>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {(sectionsByTab.get(activeSection) || []).map((s) => (
                                    <FormGroup key={s.id} className="mb-3">
                                        <Label htmlFor={s.id} className="fw-semibold">
                                            {s.label}
                                        </Label>

                                        {s.type === "Region" && (
                                            <FormGroup switch className="mb-3">
                                                <Input
                                                    type="switch"
                                                    checked={!!sectionValues[s.field]}
                                                    onChange={(e) => setFieldValue(s.field, e.target.checked)}
                                                />
                                                <span>{!!sectionValues[s.field] ? "Visible" : "Hidden"}</span>
                                            </FormGroup>
                                        )}

                                        {s.type === "TextArea" && (
                                            <RichTextEditor
                                                key={generateFieldKey(s.field)} // forces remount + placeholder reset when content becomes empty
                                                value={coerceToString(sectionValues[s.field])}
                                                onChange={(html) => setFieldValue(s.field, html)}
                                                placeholder="Start typing…"
                                                minHeight={180}
                                                className="border"
                                                ariaLabel={s.label}
                                            />
                                        )}

                                        {s.type === "Text" && (
                                            <Input
                                                id={s.id}
                                                type="text"
                                                value={coerceToString(sectionValues[s.field])}
                                                onChange={(e) => setFieldValue(s.field, e.target.value)}
                                                placeholder="Enter text…"
                                                aria-label={s.label}
                                            />
                                        )}

                                        {s.type === "Lookup" && (
                                            <Input
                                                id={s.id}
                                                type="select"
                                                value={coerceToString(sectionValues[s.field])}
                                                onChange={(e) => setFieldValue(s.field, e.target.value)}
                                            >
                                                <option value="" disabled>
                                                    Select…
                                                </option>
                                                {(s.options || []).map((opt) => (
                                                    <option key={opt.id ?? opt.value ?? opt.label} value={coerceToString(opt.value)}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    </FormGroup>
                                ))}
                            </Form>
                        </>
                    ) : (
                        <div className="text-muted mt-3">No custom sections for this template.</div>
                    )}
                </>
            )}
        </div>
    );
};

export default QuoteContent;
